import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import menuIcon from "../../assets/icons/menubar.png";
import userImage from "../../assets/images/user.png";
import { ProfilePopup } from "./AvatarOverlay";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Avatar,
  Skeleton,
} from "@mui/material";

const menuItems = [
  { text: "Home", path: "/user/home" },
  { text: "Claim History", path: "/user/search-history" },
  { text: "Report Found Item", path: "/user/report-found-item" },
  { text: "Messages", path: "/user/notification" },
  { text: "Guide", path: "/user/guide" },
  { text: "About Us", path: "/user/about-us" },
];

const Sidebar = ({ isOpen, toggleSidebar, openOverlayModal, profilePhoto: propProfilePhoto, firstName: propFirstName, loading: propLoading }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const location = useLocation();

  // Use props if available, otherwise get from localStorage as fallback
  const profilePhoto = propProfilePhoto || localStorage.getItem("profilePhoto");
  let firstName = propFirstName || "User";
  const loading = propLoading || false;
  if (!propFirstName) {
    try {
      const displayName = localStorage.getItem("displayName");
      if (displayName && typeof displayName === "string") {
        const parts = displayName.trim().split(" ");
        if (parts[0]) firstName = parts[0];
      }
    } catch (e) {
      firstName = "User";
    }
  }

  const toggleProfilePopup = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const index = menuItems.findIndex(item => item.path === currentPath);
    if (index !== -1) {
      setSelectedIndex(index);
    }
  }, [location.pathname]);

  const handleListItemClick = (event, index, path) => {
    setSelectedIndex(index);
    navigate(path);
    if (isMobile) {
      setTimeout(() => {
        toggleSidebar();
      }, 300);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={isMobile ? () => setTimeout(toggleSidebar, 300) : () => {}}
      variant={isMobile ? "temporary" : "persistent"}
      ModalProps={{
        BackdropProps: { invisible: true },
      }}
      sx={{
        width: isMobile ? "85%" : 300,
        flexShrink: 0,
        transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
        bgcolor: '#FBFFE9',
        "& .MuiDrawer-paper": {
          width: isMobile ? "85%" : 300,
          boxSizing: "border-box",
          bgcolor: "#FBFFE9",
          border: "none",
          boxShadow: "8px 0 32px 0 rgba(81, 109, 79, 0.12)",
          borderTopRightRadius: "12px",
          borderBottomRightRadius: "12px",
          transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          overflow: 'hidden',
        },
      }}
    >
      {isOpen && (
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          px: { xs: 2, sm: 4 },
          pl: { xs: 3, sm: 4 },
          minHeight: { xs: 56, sm: 64 },
          mt: '10px'
        }}>
          <IconButton 
            edge="start"
            onClick={toggleSidebar} 
            sx={{ 
              color: "#516d4f", 
              padding: { xs: '6px', sm: '8px' },
              mr: 2,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img 
              src={menuIcon} 
              alt="Menu" 
              style={{ 
                width: isMobile ? 28 : 32, 
                height: isMobile ? 28 : 32,
                display: 'block'
              }} 
            />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              color: "#516d4f",
              mr: 1,
              fontSize: { xs: "20px", sm: "24px" },
              fontFamily: "Poppins, Helvetica",
              letterSpacing: 0.5,
              display: 'flex',
              alignItems: 'center',
              height: { xs: '35px', sm: '40px' }
            }}
          >
            UFind
          </Typography>
        </Box>
      )}

      <List sx={{ mt: 2 }}>
        {menuItems.map((item, index) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={selectedIndex === index}
              onClick={(event) => handleListItemClick(event, index, item.path)}
              sx={{
                py: 2,
                pl: { xs: 5, sm: 6 },
                "&.Mui-selected": { bgcolor: "rgba(81, 109, 79, 0.08)" },
                "&:hover": { bgcolor: "rgba(81, 109, 79, 0.12)" },
              }}
            >
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: "Bold",
                    fontSize: isMobile ? "16px" : "20px",
                    color: "#516d4f",
                    ml: { xs: 3.5, sm: 4.5 },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Profile section at bottom - only on mobile */}
      {isMobile && isOpen && (
        <Box sx={{ 
          mt: 'auto', 
          mb: 2, 
          px: 3,
          position: 'relative'
        }}>
          {loading ? (
            <Skeleton 
              variant="rounded" 
              width="100%" 
              height={56} 
              sx={{ borderRadius: '20px' }}
            />
          ) : (
            <IconButton
              onClick={toggleProfilePopup}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                px: 2.5,
                py: 2,
                borderRadius: '20px',
                background: isProfileOpen ? '#e1ecc8' : '#ffffff',
                transition: 'all 0.3s ease',
                justifyContent: 'flex-start',
                boxShadow: '0 4px 16px rgba(81, 109, 79, 0.4), 0 2px 8px rgba(81, 109, 79, 0.3)',
                '&:hover': {
                  background: '#e1ecc8',
                  boxShadow: '0 6px 20px rgba(81, 109, 79, 0.5), 0 3px 12px rgba(81, 109, 79, 0.4)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 4px 16px rgba(81, 109, 79, 0.4), 0 2px 8px rgba(81, 109, 79, 0.3)',
                },
              }}
            >
              <Avatar
                alt="User Profile"
                src={profilePhoto || userImage}
                sx={{ 
                  width: 40, 
                  height: 40,
                  boxShadow: '0 2px 8px rgba(81, 109, 79, 0.3)'
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Poppins, Helvetica",
                  fontWeight: 700,
                  color: "#516d4f",
                  fontSize: "16px",
                  ml: 2,
                  letterSpacing: 0.3,
                }}
              >
                {`Hi, ${firstName}!`}
              </Typography>
            </IconButton>
          )}

          {/* Profile Popup */}
          {isProfileOpen && !loading && (
            <Box
              sx={{
                position: "absolute",
                bottom: "70px",
                left: '24px',
                right: '24px',
                zIndex: 1300,
              }}
            >
              <ProfilePopup openOverlayModal={openOverlayModal} />
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
